/**
 * Screen-space collision resolution for the personnel name labels on the live
 * canvas.
 *
 * Personnel names are the ONLY text drawn on this canvas -- stations, symbols
 * and machines carry their names inside popovers, not on the plan -- so this
 * is the whole of the label problem. At fit-zoom on a 400x250m site (~1.8 px
 * per metre) a ~48px name label covers ~27m of ground, so two workers 10m
 * apart produce two labels lying almost exactly on top of each other and
 * neither is readable.
 *
 * Like personnelClustering.js this works entirely in SCREEN PIXELS on
 * already-projected points, so labels come back on their own as the operator
 * zooms in and the pixel gaps widen. The two phases compose; neither knows
 * about the other's rules.
 *
 * A suppressed label is never lost: it returns on hover or selection
 * (`forcedIds`, which bypass collision entirely), and the marker itself is
 * always drawn.
 *
 * STABILITY: with the poll moving workers every 2s, a pure greedy pass lets
 * two overlapping labels swap which one wins on alternating polls -- visible
 * as a name flickering between two workers. `previousVisibleIds` damps that by
 * giving last poll's winners first pick, so the set only changes when the
 * geometry actually changes, not when the tie-break does.
 */

// Mirrors PersonnelMarker's label styling. Kept here rather than imported from
// the component because these are the numbers the geometry needs, and a change
// to either must be made deliberately in both.
export const LABEL_FONT_SIZE = 12;
export const LABEL_HEIGHT_PX = 20; // 2px padding x2 + ~16px line box
export const LABEL_GAP_PX = 4; // marginTop between icon and label
export const LABEL_PADDING_PX = 12; // 6px horizontal padding x2
export const MARKER_HALF_PX = 10; // 20px RiUser3Fill icon
export const CLUSTER_HALF_PX = 14; // 28px cluster badge

// Full-width ranges (CJK ideographs, kana, full-width forms). Worker names on
// this system are Chinese, so treating every character as ~0.6em -- the usual
// latin estimate -- underestimates a real label by ~40% and lets labels that
// visibly overlap both survive.
const FULL_WIDTH = /[⺀-鿿가-힯＀-￯]/;

export const estimateLabelWidth = (text) =>
  LABEL_PADDING_PX +
  [...String(text ?? "")].reduce(
    (width, char) =>
      width + (FULL_WIDTH.test(char) ? LABEL_FONT_SIZE : LABEL_FONT_SIZE * 0.6),
    0,
  );

/**
 * The label's box: centred on the marker's x, hanging below the icon. Matches
 * PersonnelMarker's `top: 100%; translateX(-50%); marginTop: 4`.
 */
export const labelBox = ({ x, y, text }) => {
  const halfWidth = estimateLabelWidth(text) / 2;
  const top = y + MARKER_HALF_PX + LABEL_GAP_PX;
  return {
    left: x - halfWidth,
    right: x + halfWidth,
    top,
    bottom: top + LABEL_HEIGHT_PX,
  };
};

/** The marker glyph's own box, used as an obstacle labels must not cover. */
export const markerBox = ({ x, y }, half = MARKER_HALF_PX) => ({
  left: x - half,
  right: x + half,
  top: y - half,
  bottom: y + half,
});

// Touching edges do not count as a collision.
const intersects = (a, b) =>
  a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;

const SEVERITY = { active: 0, offline: 1, warning: 2, sos: 3 };
const severityOf = (status) => SEVERITY[status] ?? 0;

/**
 * @param {object} params
 * @param {Array<{id:*, x:number, y:number, text:string, status:string}>}
 *   params.labels candidate labels, positioned at their MARKER's centre.
 * @param {Array<{left,right,top,bottom}>} [params.obstacles] boxes already
 *   occupied -- the caller passes every marker glyph, so a label may not be
 *   dropped on top of a worker.
 * @param {Array} [params.forcedIds] hovered/selected: shown unconditionally.
 * @param {Array} [params.previousVisibleIds] last resolution's output; supplies
 *   the anti-flicker stickiness.
 * @returns {Set} ids whose label should render.
 */
export const resolveLabelVisibility = ({
  labels = [],
  obstacles = [],
  forcedIds = [],
  previousVisibleIds = [],
}) => {
  const forced = new Set(forcedIds);
  const sticky = new Set(previousVisibleIds);

  // Priority order, most-deserving first: forced (hover/selection) > whatever
  // was readable last poll > worse status > stable id tie-break.
  const rank = (label) => [
    forced.has(label.id) ? 0 : 1,
    sticky.has(label.id) ? 0 : 1,
    -severityOf(label.status),
  ];
  const ordered = [...labels].sort((a, b) => {
    const ra = rank(a);
    const rb = rank(b);
    for (let i = 0; i < ra.length; i += 1) {
      if (ra[i] !== rb[i]) return ra[i] - rb[i];
    }
    return String(a.id).localeCompare(String(b.id));
  });

  const occupied = [...obstacles];
  const visible = new Set();

  ordered.forEach((label) => {
    const box = labelBox(label);
    if (forced.has(label.id) || !occupied.some((other) => intersects(box, other))) {
      visible.add(label.id);
      occupied.push(box);
    }
  });

  return visible;
};
